/* eslint-disable no-restricted-imports */
import { useEffect, useRef } from "react";
import {
  HotkeyCallback,
  useHotkeys as useHotkeysLib,
  useHotkeysContext,
  Options,
} from "react-hotkeys-hook";

export enum HotkeyScope {
  Overlay = "overlay",
  Page = "page",
}

export type UseHotKeys = {
  key: string;
  callback: HotkeyCallback;
  options?: Options;
};

export /**
 * Represents a keyboard shortcut
 */
type HotkeyShortcut = {
  key: string;
  label: string;
  options?: UseHotKeys["options"];
};

/**
 * Central registry of all keyboard shortcuts used in the application
 */
export const HOTKEYS = {
  devtools: {
    toggle: {
      key: "mod+shift+d",
      label: "Toggle devtools",
    },
  },
  global: {
    lockScreen: {
      key: "f12, mod+0",
      label: "Lock screen",
      options: {
        enableOnFormTags: true,
        enableOnContentEditable: true,
      },
    },
  },
  page: {
    search: {
      key: "/",
      label: "Search",
      options: { useKey: true },
    },
  },
  overlay: {
    close: {
      key: "esc",
      label: "Close",
    },
    submit: {
      key: "enter",
      label: "Submit",
    },
  },
  scoll: {
    spacebarPressToGrab: {
      key: "space",
      label: "Grab to scroll",
      options: {
        keydown: true,
        keyup: false,
        useKey: true,
      },
    },
    spacebarReleaseGrab: {
      key: "space",
      label: "Release scroll grab",
      options: {
        keydown: false,
        keyup: true,
        useKey: true,
      },
    },
  },
  bundleServices: {
    cancel: {
      key: "backspace",
      label: "Cancel service selection",
    },
    navigate: {
      key: "up, down, shift+up, shift+down",
      label: "Navigate service options",
    },
    selectCurrent: {
      key: "shift+enter",
      label: "Select current service",
    },
    submit: {
      key: "enter",
      label: "Submit service selection",
    },
    resetIndex: {
      key: "tab",
      label: "Reset index",
    },
  },
  scheduler: {
    toggleSidebar: {
      key: "`",
      label: "Toggle sidebar",
      options: { useKey: true },
    },
    viewShortcuts: {
      key: "?",
      label: "View all shortcuts",
      options: { useKey: true },
    },
    close: {
      key: "esc",
      label: "Close",
    },
    search: {
      key: "s",
      label: "Search",
    },
    today: {
      key: "t",
      label: "Today",
    },
    checkInQueue: {
      key: "q",
      label: "Check-in queue",
    },
    waitlist: {
      key: "w",
      label: "Appointment waitlist",
    },
    requested: {
      key: "r",
      label: "Client booked appointments",
    },
    deletedAppointments: {
      key: "d",
      label: "Deleted appointments",
    },
    bulkUpdateAppointments: {
      key: "b",
      label: "Bulk update appointments",
    },
    dropoffPickup: {
      key: "p",
      label: "Drop-offs & pick-ups",
    },
    moveViewRight: {
      key: "mod+right",
      label: "Move view right",
    },
    moveViewLeft: {
      key: "mod+left",
      label: "Move view left",
    },
    moveViewUp: {
      key: "mod+up",
      label: "Move view up",
    },
    moveViewDown: {
      key: "mod+down",
      label: "Move view down",
    },
    scrollRight: {
      key: "right",
      label: "Scroll view right",
    },
    scrollLeft: {
      key: "left",
      label: "Scroll view left",
    },
    scrollUp: {
      key: "up",
      label: "Scroll view up",
    },
    scrollDown: {
      key: "down",
      label: "Scroll view down",
    },
    createAppointment: {
      key: "a",
      label: "Create appointment",
    },
    createEvent: {
      key: "e",
      label: "Create event",
    },
    filters: {
      key: "f",
      label: "View filters",
    },
    settings: {
      key: "!",
      label: "View settings",
      options: { useKey: true },
    },
    copy: {
      key: "mod+c",
      label: "Copy appointment/event",
    },
    reschedule: {
      key: "mod+x",
      label: "Reschedule appointment/event",
    },
    undo: {
      key: "mod+z",
      label: "Undo appointment update",
    },
    redo: {
      key: "mod+shift+z",
      label: "Redo appointment update",
    },
  },
  medicalRecords: {
    toggleSidebar: {
      key: "`",
      label: "Toggle sidebar",
      options: { useKey: true },
    },
    viewShortcuts: {
      key: "?",
      label: "View all shortcuts",
      options: { useKey: true },
    },
    close: {
      key: "esc",
      label: "Close",
    },
    openHistory: {
      key: "h",
      label: "Open history",
    },
    openTranscription: {
      key: "v",
      label: "Voice dictation",
    },
    navigateDown: {
      key: "mod+down",
      label: "Navigate down section",
      options: {
        enableOnFormTags: true,
        enableOnContentEditable: true,
      },
    },
    navigateUp: {
      key: "mod+up",
      label: "Navigate up section",
      options: {
        enableOnFormTags: true,
        enableOnContentEditable: true,
      },
    },
    navigateRight: {
      key: "mod+right",
      label: "Navigate right section",
      options: {
        enableOnFormTags: true,
        enableOnContentEditable: true,
      },
    },
    navigateLeft: {
      key: "mod+left",
      label: "Navigate left section",
      options: {
        enableOnFormTags: true,
        enableOnContentEditable: true,
      },
    },
    openInvoice: {
      key: "i",
      label: "Open invoice",
    },
    multiSelect: {
      key: "mod+click",
      label: "Multi-select",
    },
  },
  dentalChart: {
    close: {
      key: "esc",
      label: "Close dental chart / clear selection",
    },
    multiSelect: {
      key: "mod+click",
      label: "Multi-select teeth",
    },
    previousTooth: {
      key: "left, up",
      label: "Previous tooth",
    },
    nextTooth: {
      key: "right, down",
      label: "Next tooth",
    },
    jumpToTooth: {
      key: "t",
      label: "Jump to tooth",
    },
    statusUnexamined: {
      key: "u",
      label: "Set status: unexamined",
    },
    statusNormal: {
      key: "n",
      label: "Set status: normal",
    },
    statusAbnormal: {
      key: "a",
      label: "Set status: abnormal",
    },
    statusMissing: {
      key: "m",
      label: "Set status: missing",
    },
    statusExtracted: {
      key: "x",
      label: "Set status: extracted",
    },
    flagExtraction: {
      key: "shift+x",
      label: "Toggle extraction flag",
    },
    flagMonitoring: {
      key: "shift+w",
      label: "Toggle monitoring flag",
    },
    flagTreatment: {
      key: "shift+t",
      label: "Toggle treatment flag",
    },
    mobility: {
      key: "l",
      label: "Set mobility",
    },
    calculus: {
      key: "c",
      label: "Set calculus",
    },
    plaque: {
      key: "p",
      label: "Set plaque",
    },
    gingivitis: {
      key: "g",
      label: "Set gingivitis",
    },
    furcation: {
      key: "f",
      label: "Set furcation",
    },
    boneLoss: {
      key: "b",
      label: "Set bone loss",
    },
    fracture: {
      key: "shift+f",
      label: "Set fracture",
    },
    enamel: {
      key: "shift+e",
      label: "Set enamel",
    },
    resorption: {
      key: "shift+r",
      label: "Set resorption",
    },
    focusPocketDepth: {
      key: "d",
      label: "Focus pocket depth",
    },
    focusRecession: {
      key: "r",
      label: "Focus recession",
    },
    surfaceToggle: {
      key: "alt+s",
      label: "Toggle surface inputs",
    },
    focusNotes: {
      key: "shift+n",
      label: "Focus notes",
    },
    addAnnotation: {
      key: "alt+click",
      label: "Add annotation",
    },
    quadrant1: {
      key: "alt+1",
      label: "Zoom quadrant 1",
    },
    quadrant2: {
      key: "alt+2",
      label: "Zoom quadrant 2",
    },
    quadrant3: {
      key: "alt+3",
      label: "Zoom quadrant 3",
    },
    quadrant4: {
      key: "alt+4",
      label: "Zoom quadrant 4",
    },
    quadrantAll: {
      key: "alt+0",
      label: "Full chart view",
    },
    viewShortcuts: {
      key: "?",
      label: "View all shortcuts",
      options: { useKey: true },
    },
  },
};

/**
 * Private base hook to handle keyboard shortcuts
 */
const useHotkeys = (params: UseHotKeys) => {
  const { key, callback, options } = params;

  const callbackRef = useRef(callback);

  // Update the callback ref when the callback changes.
  // This is to ensure that the callback for the hotkey is always the latest callback while at the
  // same time not needing to use the dependency array for useHotkeys.
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useHotkeysLib(
    key,
    (event, hotkeysEvent) => {
      callbackRef.current(event, hotkeysEvent);
    },
    options,
  );
};

type UseBaseHotkeys = {
  shortcut: HotkeyShortcut;
  callback: HotkeyCallback;
  scope: HotkeyScope | undefined;
  isEnabled?: boolean;
};

const useBaseHotkeys = (params: UseBaseHotkeys) => {
  const { shortcut, callback, scope, isEnabled = true } = params;

  useHotkeys({
    key: shortcut.key,
    callback,
    options: {
      scopes: scope ? [scope] : undefined,
      enabled: isEnabled,
      ...shortcut.options,
    },
  });
};

type UsePublicHotkeys = {
  shortcut: HotkeyShortcut;
  callback: HotkeyCallback;
  isEnabled?: boolean;
};

/**
 * Hook for registering keyboard shortcuts at the global level
 *
 * Will be active on all pages and overlay components
 */
export const useGlobalHotkeys = (params: UsePublicHotkeys) => {
  useBaseHotkeys({
    ...params,
    scope: undefined,
  });
};

/**
 * Hook for registering keyboard shortcuts at the page level
 *
 * Will be active on all pages
 */
export const usePageHotkeys = (params: UsePublicHotkeys) => {
  useBaseHotkeys({
    ...params,
    scope: HotkeyScope.Page,
  });
};

/**
 * Hook for registering keyboard shortcuts at the overlay level
 *
 * Will be active on all overlay components
 */
export const useOverlayHotkeys = (params: UsePublicHotkeys) => {
  useBaseHotkeys({
    ...params,
    scope: HotkeyScope.Overlay,
  });
};

export type { HotkeyCallback };

export { useHotkeysContext };
